import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  Grid,
  IconButton,
  Slide,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  Accessibility,
  AllInclusive,
  Check,
  Close as CloseIcon,
  DirectionsRun,
  EmojiEvents,
  FitnessCenter,
  Home,
  LocalFireDepartment,
  Place,
  SelfImprovement,
  Straighten,
  SyncAlt
} from '@mui/icons-material';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import BodyMap from 'components/play/BodyMap';
import StopMotionImage from 'components/play/StopMotionImage';
import OnboardingSidebar from 'components/play/onboarding/OnboardingSidebar';
import OnboardingOptionCard from 'components/play/onboarding/OnboardingOptionCard';
import GeneratingRoutineOverlay from 'components/play/onboarding/GeneratingRoutineOverlay';
import { muscleLabel } from 'components/play/constants';
import {
  MY_PLAY_PROFILE,
  MY_WEEKLY_ROUTINE,
  PLAY_EXERCISES,
  SAVE_PLAY_PROFILE,
  GENERATE_WEEKLY_ROUTINE
} from 'api/graphql/play';
import routes from 'common/routes';

const Transition = React.forwardRef((props, ref) => <Slide direction="up" ref={ref} {...props} />);

// Wizard steps (flat). `section` groups them for the header title.
const STEPS = [
  { key: 'goal', section: 'objectives', icon: <EmojiEvents /> },
  { key: 'muscles', section: 'body', icon: <Accessibility /> },
  { key: 'data', section: 'body', icon: <Straighten /> },
  { key: 'types', section: 'training', icon: <FitnessCenter /> },
  { key: 'location', section: 'training', icon: <Place /> }
];

const GOALS = [
  { value: 'gain_muscle', icon: <FitnessCenter /> },
  { value: 'lose_fat', icon: <LocalFireDepartment /> },
  { value: 'recomp', icon: <SyncAlt /> },
  { value: 'stay_fit', icon: <SelfImprovement /> },
  { value: 'activity', icon: <DirectionsRun /> }
];

const PLACES = [
  { value: 'HOME', icon: <Home /> },
  { value: 'GYM', icon: <FitnessCenter /> },
  { value: 'BOTH', icon: <AllInclusive /> }
];

const DAYS = [2, 3, 4, 5, 6];

const EMPTY = { goal: '', muscles: [], age: '', weight: '', height: '', days: 3, types: [], location: '' };

// Campos premium: superficie elevada, hairline y foco DORADO. El TextField por defecto de MUI se
// veía plano y ajeno al resto de Play (que usa estos tokens en todas partes).
const fieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: 'surface.raised',
    borderRadius: 2.5,
    transition: 'border-color 200ms cubic-bezier(0.32,0.72,0,1)',
    '& fieldset': { borderColor: 'hairline.main' },
    '&:hover fieldset': { borderColor: 'hairline.strong' },
    '&.Mui-focused fieldset': { borderColor: 'gold.main', borderWidth: '1px' }
  },
  '& .MuiInputLabel-root.Mui-focused': { color: 'gold.main' }
};

/**
 * OTRIX Play — interactive onboarding wizard (MySchoolUAE-style, OTRIX monochrome + gold).
 * Captures goal → target muscles → body data → preferred exercises → location, then persists
 * the profile and generates the weekly routine on the backend. Reopenable from the dashboard.
 */
function PlayOnboardingWizard({ open, onClose, initialProfile = null }) {
  const { t } = useTranslation(['play']);
  const theme = useTheme();
  const navigate = useNavigate();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const userAge = useSelector((state) => state.auth.user?.age);

  const [stepIndex, setStepIndex] = useState(0);
  const [answers, setAnswers] = useState(EMPTY);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // Overlay de generación: idle | working | done | error.
  const [generating, setGenerating] = useState('idle');

  const [savePlayProfile] = useMutation(SAVE_PLAY_PROFILE);
  const [generateRoutine] = useMutation(GENERATE_WEEKLY_ROUTINE);

  // Seed answers from an existing profile (reconfigure flow) each time the wizard opens.
  useEffect(() => {
    if (!open) return;
    setStepIndex(0);
    setError('');
    if (initialProfile) {
      setAnswers({
        goal: initialProfile.goal || '',
        muscles: initialProfile.target_muscles || [],
        age: userAge ? String(userAge) : '',
        weight: initialProfile.weight_kg ? String(initialProfile.weight_kg) : '',
        height: initialProfile.height_cm ? String(initialProfile.height_cm) : '',
        days: initialProfile.days_per_week || 3,
        types: initialProfile.preferred_exercise_types || [],
        location: initialProfile.location_pref || ''
      });
    } else {
      setAnswers({ ...EMPTY, age: userAge ? String(userAge) : '' });
    }
  }, [open, initialProfile, userAge]);

  // Exercise gallery for the "types" step (all Play exercises; filtered client-side by muscles).
  const { data: exData } = useQuery(PLAY_EXERCISES, { skip: !open, fetchPolicy: 'cache-and-network' });
  const typeExercises = useMemo(() => {
    const all = exData?.playExercises || [];
    if (!answers.muscles.length) return all;
    return all.filter((e) => answers.muscles.includes(e.muscle_group));
  }, [exData, answers.muscles]);

  const step = STEPS[stepIndex];
  const isLast = stepIndex === STEPS.length - 1;

  const setField = (field, value) => setAnswers((prev) => ({ ...prev, [field]: value }));

  const toggleInArray = (field, value) =>
    setAnswers((prev) => {
      const list = prev[field];
      return { ...prev, [field]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value] };
    });

  const hasAnswer = () => {
    switch (step.key) {
      case 'goal':
        return Boolean(answers.goal);
      case 'muscles':
        return answers.muscles.length > 0;
      case 'data':
        return Number(answers.age) > 0 && Number(answers.weight) > 0 && Number(answers.height) > 0;
      case 'location':
        return Boolean(answers.location);
      default:
        return true;
    }
  };

  const handleBack = () => {
    setError('');
    if (stepIndex === 0) {
      onClose(false);
      return;
    }
    setStepIndex((i) => i - 1);
  };

  const handleFinish = async () => {
    setSubmitting(true);
    // Overlay full-screen con % simulado y frases mientras corren las dos mutations.
    setGenerating('working');
    try {
      await savePlayProfile({
        variables: {
          goal: answers.goal || null,
          location_pref: answers.location || null,
          height_cm: answers.height ? Number(answers.height) : null,
          weight_kg: answers.weight ? Number(answers.weight) : null,
          age: answers.age ? Number(answers.age) : null,
          days_per_week: answers.days || null,
          target_muscles: answers.muscles,
          preferred_exercise_types: answers.types,
          complete: true
        }
      });
      await generateRoutine({
        variables: { regenerate: true },
        refetchQueries: [{ query: MY_WEEKLY_ROUTINE }, { query: MY_PLAY_PROFILE }],
        awaitRefetchQueries: true
      });
      // El overlay corre el % a 100 y dispara handleGenerated (cierra + navega).
      setGenerating('done');
    } catch (e) {
      setGenerating('error');
    } finally {
      setSubmitting(false);
    }
  };

  // El overlay llegó a 100%: cerrar wizard y aterrizar en Mi rutina.
  const handleGenerated = () => {
    setGenerating('idle');
    onClose(true);
    navigate(routes.playRoutine.path, { state: { justGenerated: true } });
  };

  const handleNext = async () => {
    if (!hasAnswer()) {
      setError(t(`onboarding.errors.${step.key}`, { ns: 'play', defaultValue: t('onboarding.errors.generic', { ns: 'play', defaultValue: 'Completa este paso para continuar.' }) }));
      return;
    }
    setError('');
    if (!isLast) {
      setStepIndex((i) => i + 1);
      return;
    }
    await handleFinish();
  };

  const renderStep = () => {
    switch (step.key) {
      case 'goal':
        return (
          <Grid container spacing={2}>
            {GOALS.map((g) => (
              <Grid item xs={12} sm={6} key={g.value}>
                <OnboardingOptionCard
                  icon={g.icon}
                  title={t(`onboarding.goals.${g.value}.title`, { ns: 'play' })}
                  description={t(`onboarding.goals.${g.value}.desc`, { ns: 'play' })}
                  selected={answers.goal === g.value}
                  onClick={() => setField('goal', g.value)}
                />
              </Grid>
            ))}
          </Grid>
        );
      case 'muscles':
        return (
          <Grid container spacing={3} alignItems="flex-start">
            <Grid item xs={12} md={6}>
              <BodyMap selectedList={answers.muscles} onToggle={(en) => toggleInArray('muscles', en)} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {t('onboarding.steps.muscles.selected', { ns: 'play', count: answers.muscles.length })}
              </Typography>
              {answers.muscles.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t('onboarding.steps.muscles.emptyHint', { ns: 'play' })}
                </Typography>
              ) : (
                <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
                  {answers.muscles.map((en) => (
                    <Box
                      key={en}
                      sx={{
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 999,
                        bgcolor: 'gold.soft',
                        color: 'gold.main',
                        border: '1px solid',
                        borderColor: 'gold.main',
                        fontWeight: 700,
                        fontSize: '0.8rem'
                      }}>
                      {muscleLabel(t, en)}
                    </Box>
                  ))}
                </Stack>
              )}
            </Grid>
          </Grid>
        );
      case 'data':
        return (
          <Stack spacing={3} sx={{ maxWidth: 460, mx: 'auto' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                fullWidth
                type="number"
                label={t('onboarding.steps.data.age', { ns: 'play' })}
                value={answers.age}
                onChange={(e) => setField('age', e.target.value)}
                inputProps={{ min: 10, max: 100 }}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                type="number"
                label={t('onboarding.steps.data.weight', { ns: 'play' })}
                value={answers.weight}
                onChange={(e) => setField('weight', e.target.value)}
                inputProps={{ min: 20, max: 400, step: 0.1 }}
                sx={fieldSx}
              />
              <TextField
                fullWidth
                type="number"
                label={t('onboarding.steps.data.height', { ns: 'play' })}
                value={answers.height}
                onChange={(e) => setField('height', e.target.value)}
                inputProps={{ min: 80, max: 250, step: 0.1 }}
                sx={fieldSx}
              />
            </Stack>
            <Box>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                {t('onboarding.steps.data.days', { ns: 'play' })}
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {DAYS.map((d) => {
                  const selected = answers.days === d;
                  return (
                    <Box
                      key={d}
                      role="button"
                      aria-pressed={selected}
                      onClick={() => setField('days', d)}
                      sx={{
                        px: 2.25,
                        py: 1,
                        borderRadius: 999,
                        cursor: 'pointer',
                        fontWeight: 700,
                        bgcolor: selected ? 'gold.soft' : 'surface.raised',
                        color: selected ? 'gold.main' : 'text.secondary',
                        border: '1px solid',
                        borderColor: selected ? 'gold.main' : 'hairline.main',
                        transition: 'all 200ms cubic-bezier(0.32,0.72,0,1)',
                        '&:active': { transform: 'scale(0.97)' }
                      }}>
                      {d}
                    </Box>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        );
      case 'types':
        return (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {t('onboarding.steps.types.hint', { ns: 'play' })}
            </Typography>
            {typeExercises.length === 0 ? (
              <Box
                sx={{
                  py: 6,
                  textAlign: 'center',
                  border: '1px dashed',
                  borderColor: 'hairline.main',
                  borderRadius: 2,
                  color: 'text.secondary'
                }}>
                <Typography variant="body2">{t('onboarding.steps.types.empty', { ns: 'play' })}</Typography>
              </Box>
            ) : (
              <Grid container spacing={2}>
                {typeExercises.map((ex) => {
                  const selected = answers.types.includes(String(ex.id));
                  return (
                    <Grid item xs={6} sm={4} key={ex.id}>
                      <Box
                        role="button"
                        aria-pressed={selected}
                        onClick={() => toggleInArray('types', String(ex.id))}
                        sx={{
                          p: 1,
                          borderRadius: '14px',
                          cursor: 'pointer',
                          bgcolor: 'surface.raised',
                          border: '1px solid',
                          borderColor: selected ? 'gold.main' : 'hairline.main',
                          transition: 'transform 200ms cubic-bezier(0.32,0.72,0,1), border-color 200ms',
                          '&:hover': { transform: 'translateY(-2px)' },
                          '&:active': { transform: 'scale(0.98)' }
                        }}>
                        <Box sx={{ position: 'relative' }}>
                          <StopMotionImage
                            frame1={ex.image_url_1}
                            frame2={ex.image_url_2}
                            alt={ex.localized_name}
                            height={110}
                            playing={selected}
                          />
                          {selected && (
                            <Box
                              sx={{
                                position: 'absolute',
                                top: 6,
                                right: 6,
                                width: 22,
                                height: 22,
                                borderRadius: '50%',
                                flex: 'none',
                                bgcolor: 'gold.main',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                              }}>
                              <Check sx={{ fontSize: 15, color: 'ink.main' }} />
                            </Box>
                          )}
                        </Box>
                        <Typography variant="body2" fontWeight={700} noWrap sx={{ mt: 1 }}>
                          {ex.localized_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {muscleLabel(t, ex.muscle_group)}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>
        );
      case 'location':
        return (
          <Grid container spacing={2}>
            {PLACES.map((p) => (
              <Grid item xs={12} sm={4} key={p.value}>
                <OnboardingOptionCard
                  icon={p.icon}
                  title={t(`onboarding.locations.${p.value}.title`, { ns: 'play' })}
                  description={t(`onboarding.locations.${p.value}.desc`, { ns: 'play' })}
                  selected={answers.location === p.value}
                  onClick={() => setField('location', p.value)}
                />
              </Grid>
            ))}
          </Grid>
        );
      default:
        return null;
    }
  };

  const question = t(`onboarding.steps.${step.key}.q`, { ns: 'play' });
  const hint = step.key === 'muscles' ? t('onboarding.steps.muscles.hint', { ns: 'play' }) : '';

  let ctaLabel = t('onboarding.continue', { ns: 'play', defaultValue: 'Continuar' });
  if (submitting) {
    ctaLabel = t('onboarding.generating.title', { ns: 'play', defaultValue: 'Armando tu rutina...' });
  } else if (isLast) {
    ctaLabel = t('onboarding.finish', { ns: 'play', defaultValue: 'Crear mi rutina' });
  }

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      TransitionComponent={Transition}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : '16px',
          bgcolor: 'surface.content',
          // Sin esto, el overlay de elevación de MUI aclara el Paper y el wizard se ve gris
          // lavado frente al off-black de la pantalla de afuera.
          backgroundImage: 'none',
          border: fullScreen ? 'none' : '1px solid',
          borderColor: 'hairline.main',
          boxShadow: 'var(--otx-card-shadow-hover)',
          minHeight: { md: 620 }
        }
      }}>
      {/* Header */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderBottom: '1px solid', borderColor: 'hairline.main' }}>
        <Button onClick={() => onClose(false)} color="inherit" size="small" sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 700 }}>
          {t('onboarding.skip', { ns: 'play', defaultValue: 'Omitir por ahora' })}
        </Button>
        <Typography variant="subtitle1" fontWeight={800} sx={{ color: 'gold.main' }}>
          {t(`onboarding.sections.${step.section}`, { ns: 'play' })}
        </Typography>
        <IconButton onClick={() => onClose(false)} aria-label={t('onboarding.close', { ns: 'play', defaultValue: 'Cerrar' })}>
          <CloseIcon />
        </IconButton>
      </Stack>

      {/* Body: sidebar + content */}
      <Grid container sx={{ flex: 1 }}>
        <Grid
          item
          xs={12}
          md={4}
          sx={{
            p: 3,
            bgcolor: 'surface.overlay',
            // Inner-highlight: la misma profundidad "de hardware" de PremiumCard.
            boxShadow: 'inset 0 1px 0 var(--otx-inner-highlight)',
            borderRight: { md: '1px solid' },
            borderColor: { md: 'hairline.main' }
          }}>
          <OnboardingSidebar steps={STEPS.map((s) => s.key)} currentStep={stepIndex} icon={step.icon} question={question} hint={hint} />
        </Grid>
        <Grid item xs={12} md={8} sx={{ p: 3, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ flex: 1 }}>{renderStep()}</Box>
          {error && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </Grid>
      </Grid>

      {/* Footer */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'hairline.main' }}>
        <Button variant="outlined" color="inherit" onClick={handleBack} disabled={submitting}>
          {t('onboarding.back', { ns: 'play', defaultValue: 'Atrás' })}
        </Button>
        <Button
          sx={{ ...premiumButtonSx, minWidth: 160 }}
          onClick={handleNext}
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}>
          {ctaLabel}
        </Button>
      </Stack>

      <GeneratingRoutineOverlay
        open={generating !== 'idle'}
        phase={generating === 'idle' ? 'working' : generating}
        onComplete={handleGenerated}
        onRetry={handleFinish}
        onCancel={() => setGenerating('idle')}
      />
    </Dialog>
  );
}

PlayOnboardingWizard.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialProfile: PropTypes.shape({
    goal: PropTypes.string,
    location_pref: PropTypes.string,
    height_cm: PropTypes.number,
    weight_kg: PropTypes.number,
    days_per_week: PropTypes.number,
    target_muscles: PropTypes.arrayOf(PropTypes.string),
    preferred_exercise_types: PropTypes.arrayOf(PropTypes.string)
  })
};

export default PlayOnboardingWizard;
